/* Small typed helpers for working with the page. */

/** Element by id. Throws if missing, so a renamed id fails loudly, not silently. */
export function byId<T extends HTMLElement = HTMLElement>(id: string): T {
  const node = document.getElementById(id);
  if (!node) throw new Error(`CleanScore: missing element #${id}`);
  return node as T;
}

/** First match inside `root`. Throws if missing, like byId. */
export function qs<T extends HTMLElement = HTMLElement>(root: ParentNode, selector: string): T {
  const node = root.querySelector<T>(selector);
  if (!node) throw new Error(`CleanScore: missing element ${selector}`);
  return node;
}

/** Build an element from an HTML string. The caller escapes what it inserts. */
export function html<T extends HTMLElement = HTMLElement>(markup: string): T {
  const template = document.createElement("template");
  template.innerHTML = markup.trim();
  return template.content.firstElementChild as T;
}

const ESCAPES: Record<string, string> = {
  "&": "&amp;",
  "<": "&lt;",
  ">": "&gt;",
  '"': "&quot;",
  "'": "&#39;",
};

/** Escape text before it goes into innerHTML. Label text comes from OCR and
 *  Open Food Facts, so it is never trusted as markup. */
export function escapeHtml(value: unknown): string {
  return String(value).replace(/[&<>"']/g, (c) => ESCAPES[c] ?? c);
}

/** "garam masala" -> "Garam masala". */
export function capitalise(word: string): string {
  return word.charAt(0).toUpperCase() + word.slice(1);
}

export function setBusy(button: HTMLButtonElement, on: boolean, label?: string): void {
  button.classList.toggle("busy", on);
  button.disabled = on;
  const text = button.querySelector<HTMLElement>(".btn-label");
  if (text && label) text.textContent = label;
}

let toastTimer = 0;

/** Brief message at the bottom of the screen. Errors stay until replaced. */
export function toast(message: string, tone: "info" | "error" = "info"): void {
  const box = byId("toast");
  box.textContent = message;
  box.className = "toast show " + tone;
  window.clearTimeout(toastTimer);
  toastTimer = window.setTimeout(() => box.classList.remove("show"), tone === "error" ? 6000 : 2800);
}
